// =============================================================================
// Voice capture confirmation gate — pure decision logic. Issue #684.
//
// Split out of useRealtimeVoice.ts so the state machine itself is
// unit-testable without mocking RTCPeerConnection / a WebRTC data channel —
// same "extract the decision, keep the hook thin" discipline as pushGate.ts,
// grocerySyncOutcome.ts, restockMatch.ts, kin-inbox/capLogic.ts elsewhere in
// this repo, even though nothing here is Deno-only.
//
// ── What this is actually enforcing, and what it isn't ──────────────────────
//
// kin-voice-session (the Edge Function) mints an OpenAI ephemeral token and
// exits — the client talks straight to OpenAI over WebRTC after that, and no
// Kin backend is connected to the live session. So this gate runs in the
// BROWSER, not on a Kin server: it's enforced by useRealtimeVoice.ts using
// transcript/tool-call events the client already receives live, not by
// anything Kin controls server-side. That's a real limitation, not an
// oversight — see issue #725 for what genuine server-side enforcement would
// require (routing the session through a Kin-controlled relay) and why it
// isn't worth that cost today. What this DOES close: the realtime model
// over-triggering capture_reminder on ambient speech with no real
// confirmation, which was the actual failure mode #684 was about — a
// technically sophisticated user tampering with their own client to fool
// this gate is not in scope; the threat model here is the model, not her.
//
// ── The two-phase shape ──────────────────────────────────────────────────
//
// The model must call propose_reminder (a no-op registration, no write)
// before capture_reminder is honored, AND a real user transcript-completed
// event must land in between. Two failure modes this closes that a bare
// "did a user turn happen recently" timer would not:
//   - The model jumping straight to capture_reminder with no propose at all.
//   - The model calling propose_reminder, getting a user turn on a TOTALLY
//     UNRELATED reply, and treating that as consent for whatever it proposed.
// The state resets after every capture_reminder attempt (allowed or not) —
// one propose+turn pair authorizes exactly one capture, never a stream of
// them off a single confirmation.
//
// Deliberately NOT required: the propose and capture text matching exactly.
// Voice transcripts and the model's own phrasing legitimately vary between
// "here's what I'm about to add" and the final capture after she confirms —
// requiring an exact match would make legitimate rephrasing fail closed,
// which is the same wrong-direction risk #704 named for over-strict
// matching. Presence of a proposal plus an intervening real user turn is the
// bar, not string equality.
// =============================================================================

/**
 * The voice tools that WRITE and therefore need a confirmation. #888 generalized
 * this gate past capture_reminder; the issue said reuse it, don't fork it.
 *
 * ⚠️ The state records WHICH tool was proposed, not merely that something was.
 * A shared boolean would have been the obvious generalization and it introduces
 * a real bug: Kin asks "want me to add that to your list?", she says yes, and
 * the model calls remember_fact instead. `proposed && userTurn` would be true,
 * so a consent given for a reminder would authorize a memory write. Naming the
 * tool is what makes one gate safe for two tools.
 */
export type VoiceWriteTool =
  | "capture_reminder"
  | "remember_fact"
  | "save_todo"
  // #1686 — voice's fourth write, and the heaviest. A to-do sits on a list; an
  // event lands on the surface her whole Today tab renders from and feeds her
  // 7am morning note, so a wrong one is a text about a thing that is not
  // happening. Nothing else about this gate changes: naming the tool is what
  // keeps a yes she gave about a to-do from authorizing a row on her calendar,
  // which is the same reason the field is a tool name and not a boolean.
  | "create_event";

export interface VoiceCaptureGateState {
  /** Which tool a propose is outstanding for, or null. */
  proposedFor: VoiceWriteTool | null;
  userTurnSinceLastPropose: boolean;
  /**
   * Correlation id for the outstanding proposal (#933), or null.
   *
   * The caller mints it and passes it in — this module stays pure, so the same
   * inputs always give the same state and the tests need no clock or RNG. It
   * travels to the write and is recorded on the row, which is what makes a
   * duplicated tool call a no-op instead of a second row.
   */
  proposeId: string | null;
}

export const INITIAL_VOICE_CAPTURE_GATE_STATE: VoiceCaptureGateState = {
  proposedFor: null,
  userTurnSinceLastPropose: false,
  proposeId: null,
};

/**
 * Call when the model invokes a propose_* tool.
 *
 * A second propose REPLACES the first rather than stacking: if Kin proposes a
 * reminder, then proposes a fact before any confirmation, only the fact is
 * outstanding. Otherwise a stale proposal could be redeemed by a much later
 * "yes" that was plainly about something else.
 *
 * ⚠️ The propose id is replaced along with everything else. It has to be: the
 * id identifies THIS proposal, and carrying the previous one forward would make
 * a genuinely new write look like a duplicate of the abandoned one and be
 * silently dropped by the unique index.
 */
export function onPropose(
  _state: VoiceCaptureGateState,
  tool: VoiceWriteTool,
  proposeId: string | null = null,
): VoiceCaptureGateState {
  return { proposedFor: tool, userTurnSinceLastPropose: false, proposeId };
}

/**
 * Back-compat alias for the reminder path, so the existing call site and its
 * tests keep reading naturally.
 */
export function onProposeReminder(
  state: VoiceCaptureGateState,
  proposeId: string | null = null,
): VoiceCaptureGateState {
  return onPropose(state, "capture_reminder", proposeId);
}

/**
 * Clear refusals and clear hesitations. Matching one WITHDRAWS the outstanding
 * proposal — issue #1686, found by Copilot on #1695.
 *
 * ── What was wrong ──────────────────────────────────────────────────────────
 *
 * `onUserTurn` counted ANY completed transcription as the turn that arms a
 * write. It is deliberately not a yes-detector (see the header: the propose and
 * the capture are not required to match, because legitimate rephrasing must not
 * fail closed). But "any turn" also counts "no thanks" — so Kin could ask, she
 * could refuse, the model could call the write anyway, and the gate would allow
 * it. #1686's own DONE WHEN says "Saying no creates nothing", and nothing
 * enforced it but the persona.
 *
 * ── Why a DENYLIST and not a yes-classifier ─────────────────────────────────
 *
 * Requiring a recognised "yes" fails in the expensive direction: "mhm", "go on
 * then", "please do", "yeah alright" are all consent, and a classifier that
 * misses one silently stops her being able to save anything. Matching clear
 * REFUSAL fails in the cheap direction instead — every false positive costs one
 * re-ask and nothing else, and no false positive can ever cause a write.
 *
 * ⚠️ So this list may be over-inclusive and must never be under-inclusive.
 * "no, wait, yes do it" matches and drops the proposal; Kin asks again. That is
 * the correct trade every time.
 *
 * This is NOT a substitute for the model behaving — it is the backstop for when
 * it does not, which is the same thing the two-step itself is for.
 */
const REFUSAL_RE =
  /\b(no|nope|nah|naw|don'?t|do not|never ?mind|forget it|cancel|stop|skip( it)?|not now|not yet|maybe later|another time|leave it|it'?s fine|hold on|wait|actually)\b/i;

/** True when a turn reads as a refusal or a hesitation rather than consent. */
export function isRefusal(transcript: string | null | undefined): boolean {
  if (typeof transcript !== "string") return false;
  return REFUSAL_RE.test(transcript);
}

/**
 * Call on every real user-transcript-completed event. A user turn before any
 * propose doesn't pre-authorize a later capture — it only counts once a
 * propose is already on record.
 *
 * ⚠️ `transcript` is OPTIONAL, and omitting it keeps the pre-#1686 behaviour
 * exactly. Every existing caller and test that passes only the state is
 * unchanged; the hook passes the real transcript, so all four voice writes gain
 * the refusal backstop together.
 */
export function onUserTurn(
  state: VoiceCaptureGateState,
  transcript?: string | null,
): VoiceCaptureGateState {
  if (!state.proposedFor) return state;
  // A refusal withdraws the proposal outright rather than merely failing to arm
  // it. Anything less leaves a live proposal she has already said no to, which a
  // later unrelated "yeah" could then redeem.
  if (isRefusal(transcript)) return INITIAL_VOICE_CAPTURE_GATE_STATE;
  return { ...state, userTurnSinceLastPropose: true };
}

export interface VoiceCaptureDecision {
  allowed: boolean;
  /** Always resets to INITIAL — one propose+turn pair authorizes one capture. */
  nextState: VoiceCaptureGateState;
  /**
   * The id of the proposal being redeemed (#933), or null when the write is not
   * allowed.
   *
   * ⚠️ Null on a rejected write ON PURPOSE. A rejected call must not carry an id
   * anywhere near the database: writing with it would burn that id, and then the
   * legitimate write the model makes after asking her properly would come back
   * as a `duplicate` and be dropped.
   */
  proposeId: string | null;
}

/**
 * Call when the model invokes a write tool.
 *
 * Allowed only when a propose is outstanding FOR THIS TOOL and a real user turn
 * landed after it. State always resets, allowed or not — one propose+turn pair
 * authorizes exactly one write, never a stream off a single confirmation.
 */
export function decideVoiceWrite(
  state: VoiceCaptureGateState,
  tool: VoiceWriteTool,
): VoiceCaptureDecision {
  const allowed = state.proposedFor === tool && state.userTurnSinceLastPropose;
  return {
    allowed,
    nextState: INITIAL_VOICE_CAPTURE_GATE_STATE,
    proposeId: allowed ? state.proposeId : null,
  };
}

/** Back-compat alias for the reminder path. */
export function decideCaptureReminder(state: VoiceCaptureGateState): VoiceCaptureDecision {
  return decideVoiceWrite(state, "capture_reminder");
}
